import { Box, Button, Icons, iconSize, Typography, webFonts, webPalette } from '@munifocus/ui';
import { emailError, validationMessageKey } from '@munifocus/utils';
import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiLang, subscribeBulletin } from '../content/lead-capture';

type SignupStatus = 'idle' | 'submitting' | 'done' | 'error';

/**
 * Owns the email field + submit lifecycle for a bulletin subscription. Both the
 * bulletin hero band and the footer reuse this so the request shape lives in one
 * place. The confirmation copy differs by outcome: `pending` (a confirmation
 * email was sent for double opt-in) asks the visitor to check their inbox;
 * `subscribed` (e.g. an already-confirmed address) tells them they've been added.
 */
export function useBulletinSubscribe() {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<SignupStatus>('idle');
  const [pending, setPending] = useState(true);
  // Shown once the visitor leaves the field or tries to submit, never mid-word.
  const [checked, setChecked] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const errorId = useId();

  const code = emailError(email);
  const errorText = checked && code ? t(validationMessageKey('email', code)) : null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (status === 'submitting') return;
    setChecked(true);
    if (code) {
      inputRef.current?.focus();
      return;
    }
    const value = email.trim();
    setStatus('submitting');
    try {
      const result = await subscribeBulletin(value, apiLang(i18n.language));
      setPending(result.status === 'pending');
      setStatus('done');
      setEmail('');
      setChecked(false);
    } catch {
      setStatus('error');
    }
  };

  // Which confirmation string to show once `status === 'done'`: the "check your
  // inbox" opt-in copy when a confirmation email was sent, else "you're added".
  const confirmKey = pending
    ? 'web.bulletinSubscription.checkInbox'
    : 'web.bulletinSubscription.added';

  /** Spread onto the email `<input>`: value, blur check, aria wiring, focus target. */
  const inputProps = {
    ref: inputRef,
    value: email,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value),
    onBlur: () => {
      if (email.trim()) setChecked(true);
    },
    'aria-invalid': errorText ? true : undefined,
    'aria-describedby': errorText ? errorId : undefined,
  };

  return { email, setEmail, status, submit, confirmKey, pending, inputProps, errorText, errorId };
}

/**
 * Compact footer subscribe form on the dark green band. The bulletin page keeps
 * its own bespoke hero treatment but shares {@link useBulletinSubscribe}.
 */
export function BulletinSubscribe() {
  const { t } = useTranslation();
  const { status, submit, confirmKey, inputProps, errorText, errorId } = useBulletinSubscribe();

  return (
    <Box>
      <Box
        sx={{
          fontSize: 12,
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          color: webPalette.teal,
          mb: 1.75,
          fontWeight: 600,
        }}
      >
        {t('web.bulletinSubscription.footerHeading')}
      </Box>
      <Typography sx={{ color: webPalette.onDarkSubtle, fontSize: 15, mb: 2, maxWidth: '32ch' }}>
        {t('web.bulletinSubscription.footerBody')}
      </Typography>
      {status === 'done' ? (
        <Box
          role="status"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            color: webPalette.teal,
            fontWeight: 600,
            fontSize: 15,
          }}
        >
          <Icons.Check size={iconSize.md} />
          {t(confirmKey)}
        </Box>
      ) : (
        <Box
          component="form"
          onSubmit={submit}
          // Our own check, so the message is translated and matches the API's rule.
          noValidate
          sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', maxWidth: 360 }}
        >
          <Box
            component="input"
            type="email"
            required
            autoComplete="email"
            {...inputProps}
            placeholder={t('web.bulletinSubscription.placeholder')}
            // A placeholder is not a label — it disappears as soon as the
            // visitor types (WCAG 1.3.1 / 4.1.2).
            aria-label={t('web.bulletinSubscription.placeholder')}
            sx={{
              flex: 1,
              minWidth: 180,
              backgroundColor: webPalette.surfaceOnDarkFaint,
              border: `1px solid ${webPalette.borderOnDarkInput}`,
              color: webPalette.onDark,
              borderRadius: '8px',
              p: '12px 14px',
              fontFamily: webFonts.sans,
              fontSize: 14.5,
              '::placeholder': { color: webPalette.onDarkDim },
              // `outline: 'none'` used to remove the only focus indicator this
              // input had; the border-colour change alone is not a sufficient
              // one (WCAG 2.4.7). The theme's shared focus ring covers
              // `:focus-visible`, so only tint the surface here.
              '&:focus': {
                borderColor: webPalette.teal,
                backgroundColor: webPalette.surfaceOnDarkMuted,
              },
            }}
          />
          <Button
            type="submit"
            variant="contained"
            color="secondary"
            disabled={status === 'submitting'}
            sx={{
              backgroundColor: webPalette.teal,
              color: webPalette.greenDeep,
              '&:hover': { backgroundColor: webPalette.tealHover },
            }}
          >
            {t('web.bulletinSubscription.cta')}
          </Button>
        </Box>
      )}
      {errorText && status !== 'done' && (
        <Typography
          id={errorId}
          role="alert"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            color: webPalette.dangerOnDark,
            fontSize: 13.5,
            mt: 1.25,
          }}
        >
          <Icons.AlertTriangle size={iconSize.sm} aria-hidden="true" />
          {errorText}
        </Typography>
      )}
      {status === 'error' && (
        <Typography
          role="alert"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            color: webPalette.dangerOnDark,
            fontSize: 13.5,
            mt: 1.25,
          }}
        >
          <Icons.AlertTriangle size={iconSize.sm} aria-hidden="true" />
          {t('web.bulletinSubscription.error')}
        </Typography>
      )}
    </Box>
  );
}
